import React from 'react';
import { Song } from 'types/song';
import { useAppSelector } from 'app/store';
import {
  getAudioPlayer,
  getIsPlayerLoading,
  getPlayerCurrentAudio,
} from 'app/store/AudioPlayer/selectors';
import { DataTestId } from 'types/general.types';
import { SystemStyleObject } from '@chakra-ui/react';
import { AudioPlayControlsComponent } from './AudioPlayControlsComponent';
import { useAudioPlayHook } from './AudioPlayHook';

interface SongDetails {
  status?: Song['status'];
  title?: Song['title'];
}

interface AudioPlayControlsProps extends DataTestId {
  audioGuid: string;
  playlistAudioGuid?: string;
  parentGuid: string;
  isError?: boolean;
  isLoading: (props: {
    isAudioLoading: boolean;
    audioPlayerGuid: string;
  }) => boolean;
  songDetails?: SongDetails;
  versionSongGuid?: string;
  handleAudioLoad?: () => void;
  isPlayerTheme?: boolean;
  sx?: SystemStyleObject;
}

export const AudioPlayControls: React.FC<AudioPlayControlsProps> = ({
  audioGuid,
  playlistAudioGuid,
  parentGuid,
  isError,
  isLoading,
  songDetails,
  versionSongGuid,
  handleAudioLoad,
  isPlayerTheme,
  'data-testid': dataTestId,
  sx,
}) => {
  const { handlePlay } = useAudioPlayHook({
    audioGuid,
    parentGuid,
    songDetails,
    versionSongGuid,
    handleAudioLoad,
    playlistAudioGuid,
  });

  const { isPlaying } = useAppSelector(getAudioPlayer);
  const currentAudio = useAppSelector(getPlayerCurrentAudio);
  const isPlayerLoading = useAppSelector(getIsPlayerLoading);

  const guid = playlistAudioGuid || audioGuid;
  const activeGuid = currentAudio?.playlistAudioGuid || currentAudio?.audioGuid;

  return (
    <AudioPlayControlsComponent
      handlePlay={handlePlay}
      isPlaying={isPlaying && activeGuid === guid}
      isActive={activeGuid === guid}
      isError={isError}
      isLoading={isLoading({
        isAudioLoading: isPlayerLoading,
        audioPlayerGuid: activeGuid || '',
      })}
      isPlayerTheme={isPlayerTheme}
      data-testid={dataTestId}
      sx={sx}
    />
  );
};
